import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Row } from 'reactstrap';
import { useNavigate, useLocation } from 'react-router';
import mainContext from '../../../../../App/MainProvider/context';
import operationContext from '../../../context';
import esmrContext from '../context';
import {
  PageHeader,
  Section,
  Button,
  LinkButton,
  ButtonBar,
  TablePagination,
  SpinnerArea,
} from '../../../../../../shared/components';
import useServiceReducer from '../../../../../../shared/hooks/useServiceReducer';
import reducer, { FlowCommentsAction } from '../../Comments/reducer';
import CommentsTable from '../../Comments/CommentsTable';
import { findMapped, findMapId } from '../../../../../../shared/misc/utils';
import { FlowCommentsFilter } from '../../../../../../services/types/Flow';
import operEsmrCommentsService from '../../../../../../services/operEsmrCommentsService';
import GetFlowCurrentVersion from '../../common';

const EsmrComments: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { service, inSuperUserMode, inGuidanceServiceMode } = useContext(mainContext);
  const { operation } = useContext(operationContext);
  const { data: esmr } = useContext(esmrContext);
  if (!esmr || !esmr.content) {
    return null;
  }

  const [data, loading, filter, dispatch] = useServiceReducer(
    operEsmrCommentsService.getList,
    reducer,
    {
      operationId: operation.id,
      pageNumber: 1,
      pageSize: 5,
      queryType: null,
    } as FlowCommentsFilter,
    [inSuperUserMode(), inGuidanceServiceMode()]
  );

  const [, setExcelLoading] = useState(false);

  const queryItems = [
    { id: 1, text: t('all'), mapped: null, htmlId: 'all' },
    { id: 2, text: t('complianceIssue'), mapped: 'compliance', htmlId: 'compliance' },
    { id: 3, text: t('qualityImprovement'), mapped: 'quality', htmlId: 'quality' },
  ];

  const goToForm = () => {
    navigate(`${location.pathname}/form`, { replace: true });
  };

  const getByQuery = (queryItemId: number) => {
    const filterValue = findMapped(queryItems, queryItemId);
    const action = { type: 'QUERY', value: filterValue } as FlowCommentsAction;
    dispatch(action);
  };

  const changePage = (page: number) => {
    const action = { type: 'PAGE', value: page } as FlowCommentsAction;
    dispatch(action);
  };

  const exportExcel = () => {
    service(() => operEsmrCommentsService.exportExcel(operation.id), {
      loader: setExcelLoading,
    });
  };
  const isApproved = () => {
    const currentVersion = GetFlowCurrentVersion(esmr.content);
    if (currentVersion && currentVersion.step === esmr.steps.length) {
      return true;
    }
    return false;
  };
  const hasComments = data != null && data.totalWithoutFilter > 0;

  return (
    <Container className="dashboard">
      <PageHeader
        title={t('flowCommentsTitle', { number: operation.number, flowType: 'ESMR' })}
        side={
          <>
            <div className="mr-auto" />
            {!isApproved() && (
              <LinkButton
                text={t('createNewComment')}
                onClick={goToForm}
                className="btn btn-primary"
                icon="file-signature"
              />
            )}
          </>
        }
      />
      <Row>
        <Section>
          <div className="button-container-bar">
            <div className="centered-bar">
              <h5 className="m-3">{`${t('view')}:`}</h5>
              <ButtonBar
                id="queries"
                value={findMapId(queryItems, filter.queryType)}
                onChange={getByQuery}
                elements={queryItems}
                disabled={!hasComments}
              />
            </div>
            <div className="mr-auto" />
            {hasComments && (
              <Button
                htmlId="btnExcel"
                text={t('exportToExcel')}
                onClick={exportExcel}
                icon="file-excel"
                className="btn-tertiary"
              />
            )}
          </div>

          <SpinnerArea loading={loading}>
            {data &&
              (hasComments ? (
                <>
                  <div className="content">
                    <h4>
                      {data &&
                        t('showingFlowComments', {
                          from: data.from,
                          to: data.to,
                          total: data.total,
                        })}
                    </h4>
                  </div>
                  <CommentsTable commentListData={data.list} flowType="ESMR" />
                  <TablePagination
                    pageSize={filter.pageSize}
                    pageNumber={filter.pageNumber}
                    total={data.total}
                    onChangePage={changePage}
                  />
                </>
              ) : (
                <div className="section-no-comments">{t('flowNoComments')}</div>
              ))}
          </SpinnerArea>
        </Section>
      </Row>
    </Container>
  );
};

export default EsmrComments;
